import {
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
  startAuthentication,
  startRegistration,
} from '@simplewebauthn/browser';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/types';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ApiError, auth as authApi } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';

const HASH_PATTERN = /^[A-Z2-9]{4}-[A-Z2-9]{4}-[A-Z2-9]{4}-[A-Z2-9]{4}$/;

/**
 * Persisted across tab close so a user who navigates away while waiting for
 * admin approval can return to the same /login URL and resume polling. Uses
 * `localStorage` (not `sessionStorage`) because Telegram's in-app browser
 * frequently spawns a fresh tab when the user taps "Open in Safari/Chrome".
 */
const PENDING_KEY = 'vpn.login.pending';

interface PendingRequest {
  requestId: string;
  expiresAt: number;
}

function readPending(): PendingRequest | null {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PendingRequest;
    if (typeof parsed.requestId !== 'string' || typeof parsed.expiresAt !== 'number') {
      return null;
    }
    if (parsed.expiresAt <= Date.now()) return null;
    return parsed;
  } catch {
    return null;
  }
}

type Phase =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'waiting'; requestId: string; expiresAt: number }
  /**
   * Approved → kicked off platform-authenticator registration. Carries the
   * `approvalId` + the server-issued `registrationOptions` so the registration
   * effect (which lives in its own `useEffect`) can run independently of the
   * polling effect's lifecycle. If we drove the WebAuthn ceremony from inside
   * `tick`, `setPhase` would re-run the polling effect and its cleanup would
   * flip `cancelled` to true *before* `startRegistration` resolved — the verify
   * call would silently no-op and the page would hang on this phase.
   */
  | {
      kind: 'registering';
      approvalId: string;
      options: PublicKeyCredentialCreationOptionsJSON;
    }
  | { kind: 'rejected'; reason: string }
  | { kind: 'expired' }
  | { kind: 'error'; reason: string };

type SupportState = 'unknown' | 'ok' | 'unsupported';

export function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  // Pre-fill the hash from `?code=…` so the link from the bot lands the user
  // straight on a populated form (still no auto-submit — they tap «Отправить»).
  const [hash, setHash] = useState<string>(() => {
    if (typeof window === 'undefined') return '';
    const param = new URLSearchParams(window.location.search).get('code');
    return param ? normalizeHash(param) : '';
  });
  // Resume an in-flight approval if the tab was closed mid-wait. Without this
  // a re-submit of the same code would 404 (one-shot) or 409 (already pending).
  const [phase, setPhase] = useState<Phase>(() => {
    const pending = readPending();
    return pending
      ? { kind: 'waiting', requestId: pending.requestId, expiresAt: pending.expiresAt }
      : { kind: 'idle' };
  });
  const [error, setError] = useState<string | null>(null);
  const [support, setSupport] = useState<SupportState>('unknown');
  const [passkeyBusy, setPasskeyBusy] = useState(false);

  useEffect(() => {
    if (auth.status === 'authenticated') navigate('/', { replace: true });
  }, [auth.status, navigate]);

  // Mirror the waiting phase to localStorage so the user can close the tab
  // and come back. Cleared on every other phase — including registration,
  // since by then the request is consumed.
  useEffect(() => {
    if (phase.kind === 'waiting') {
      localStorage.setItem(
        PENDING_KEY,
        JSON.stringify({ requestId: phase.requestId, expiresAt: phase.expiresAt }),
      );
    } else {
      localStorage.removeItem(PENDING_KEY);
    }
  }, [phase]);

  // Probe platform-authenticator availability up front so we can warn the
  // user before they spend time entering a hash code on, say, an old desktop
  // browser without Windows Hello.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!browserSupportsWebAuthn()) {
        if (!cancelled) setSupport('unsupported');
        return;
      }
      try {
        const ok = await platformAuthenticatorIsAvailable();
        if (!cancelled) setSupport(ok ? 'ok' : 'unsupported');
      } catch {
        if (!cancelled) setSupport('unsupported');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Poll the approval request once we've got an id. The polling effect ONLY
  // hands off to `registering` — the WebAuthn ceremony lives in a separate
  // effect so that flipping the phase doesn't immediately tear down our
  // own work via the cleanup running on the same tick.
  useEffect(() => {
    if (phase.kind !== 'waiting') return;
    let cancelled = false;
    let timer: number | undefined;

    const tick = async () => {
      try {
        const result = await authApi.pollRequest(phase.requestId);
        if (cancelled) return;
        if (result.status === 'pending') {
          timer = window.setTimeout(tick, 2_000);
          return;
        }
        if (result.status === 'rejected') {
          setPhase({ kind: 'rejected', reason: 'Администратор отклонил запрос.' });
          return;
        }
        if (result.status === 'expired') {
          setPhase({ kind: 'expired' });
          return;
        }
        // Approved — hand off to the registration effect.
        setPhase({
          kind: 'registering',
          approvalId: result.approvalId,
          options: result.registrationOptions,
        });
      } catch (err) {
        if (cancelled) return;
        setPhase({
          kind: 'error',
          reason: err instanceof Error ? err.message : 'Не удалось проверить статус запроса.',
        });
      }
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [phase, auth, navigate]);

  // Run the WebAuthn registration ceremony when we land in `registering`.
  // This effect's `cancelled` flag only flips on unmount or another phase
  // transition, so a successful Face ID can finalize without being torn
  // down by the polling effect's cleanup.
  useEffect(() => {
    if (phase.kind !== 'registering') return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await startRegistration({ optionsJSON: phase.options });
        if (cancelled) return;
        const verified = await authApi.webauthnRegisterVerify({
          approvalId: phase.approvalId,
          response,
        });
        if (cancelled) return;
        await auth.setToken(verified.token);
        navigate('/', { replace: true });
      } catch (err) {
        if (cancelled) return;
        const reason =
          err instanceof Error
            ? err.message
            : 'Не удалось завершить регистрацию устройства.';
        setPhase({ kind: 'error', reason });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [phase, auth, navigate]);

  // Manual passkey re-auth. The silent attempt in `AuthProvider` runs without
  // a user gesture and Safari rejects it with NotAllowedError, so users with
  // an existing passkey land here and need an explicit button to retry the
  // assertion from a click handler.
  const onPasskey = async () => {
    setError(null);
    setPasskeyBusy(true);
    try {
      const options = await authApi.webauthnAssertOptions();
      const response = await startAuthentication({ optionsJSON: options });
      const verified = await authApi.webauthnAssertVerify(response);
      await auth.setToken(verified.token);
      navigate('/', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Этот ключ доступа больше не привязан к аккаунту. Войдите по коду.');
      } else if (err instanceof Error && err.name === 'NotAllowedError') {
        // User dismissed the OS prompt or the credential wasn't selected.
        setError(null);
      } else {
        setError(err instanceof Error ? err.message : 'Не удалось войти по passkey.');
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const normalized = useMemo(() => normalizeHash(hash), [hash]);
  const isValid = HASH_PATTERN.test(normalized);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isValid) {
      setError('Введите код в формате XXXX-XXXX-XXXX-XXXX.');
      return;
    }
    if (support === 'unsupported') {
      setError(
        'Этот браузер не поддерживает Face ID / Touch ID / Windows Hello. ' +
          'Откройте кабинет на устройстве с биометрией.',
      );
      return;
    }
    setError(null);
    setPhase({ kind: 'submitting' });
    try {
      const fingerprint = await auth.fingerprint();
      const deviceName = guessDeviceName();
      const resp = await authApi.hashLogin({
        hash: normalized,
        fingerprint,
        deviceName,
      });
      setPhase({
        kind: 'waiting',
        requestId: resp.requestId,
        expiresAt: new Date(resp.expiresAt).getTime(),
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.status === 404 ? 'Код не найден или уже использован.' : err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Ошибка отправки кода.');
      }
      setPhase({ kind: 'idle' });
    }
  };

  if (phase.kind === 'waiting') {
    return <WaitingScreen expiresAt={phase.expiresAt} onCancel={() => setPhase({ kind: 'idle' })} />;
  }

  if (phase.kind === 'registering') {
    return (
      <Centered>
        <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900/60 p-8 text-center">
          <Spinner />
          <h1 className="mt-4 text-xl font-semibold">Подтвердите вход</h1>
          <p className="mt-2 text-sm text-slate-400">
            Используйте Face ID, Touch ID или Windows Hello, чтобы привязать это устройство к
            кабинету.
          </p>
        </div>
      </Centered>
    );
  }

  if (phase.kind === 'rejected' || phase.kind === 'expired' || phase.kind === 'error') {
    const heading =
      phase.kind === 'error' ? 'Не удалось проверить статус' : 'Запрос не удался';
    const text =
      phase.kind === 'rejected'
        ? phase.reason
        : phase.kind === 'expired'
          ? 'Время ожидания подтверждения истекло. Попробуйте получить новый код у администратора.'
          : phase.reason;
    return (
      <Centered>
        <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-6">
          <h1 className="text-xl font-semibold text-red-300">{heading}</h1>
          <p className="mt-2 text-sm text-slate-300">{text}</p>
          <button
            className="mt-6 rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white hover:bg-brand-dark"
            onClick={() => setPhase({ kind: 'idle' })}
          >
            Попробовать снова
          </button>
        </div>
      </Centered>
    );
  }

  const submitting = phase.kind === 'submitting';

  return (
    <Centered>
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900/60 p-8 shadow-xl"
      >
        <h1 className="text-2xl font-semibold">Вход в кабинет</h1>
        <p className="mt-2 text-sm text-slate-400">
          Введите код, который выдал вам администратор. Код действует 24 часа и используется один раз.
        </p>
        {support === 'unsupported' && (
          <p className="mt-4 rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-2 text-xs text-amber-200">
            На этом устройстве недоступен Face ID / Touch ID / Windows Hello. Откройте кабинет в
            современном браузере с биометрией.
          </p>
        )}
        <p className="mt-4 rounded-lg border border-slate-700/60 bg-slate-800/40 px-3 py-2 text-xs text-slate-300">
          Откройте эту страницу в Safari, Chrome или другом обычном браузере. Во встроенном окне
          мессенджера (Telegram, WhatsApp) Face ID / Touch ID / Windows Hello часто не работают.
        </p>
        {support !== 'unsupported' && (
          <>
            <button
              type="button"
              onClick={onPasskey}
              disabled={passkeyBusy || submitting}
              className="mt-6 w-full rounded-lg border border-brand/60 bg-brand/10 py-2 text-sm font-medium text-brand hover:bg-brand/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {passkeyBusy ? 'Открываем Face ID / Touch ID…' : 'Войти через Face ID / Touch ID'}
            </button>
            <div className="mt-6 flex items-center gap-3 text-xs uppercase tracking-wider text-slate-500">
              <div className="h-px flex-1 bg-slate-800" />
              <span>или по коду</span>
              <div className="h-px flex-1 bg-slate-800" />
            </div>
          </>
        )}
        <label className="mt-6 block text-sm font-medium text-slate-300" htmlFor="hash">
          Код доступа
        </label>
        <div className="relative mt-1">
          <input
            id="hash"
            autoFocus
            autoComplete="off"
            spellCheck={false}
            inputMode="text"
            value={hash}
            onChange={(e) => setHash(normalizeHash(e.target.value))}
            placeholder="XXXX-XXXX-XXXX-XXXX"
            className="w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 pr-11 font-mono uppercase tracking-wider text-slate-100 outline-none focus:border-brand"
            maxLength={24}
          />
          <PasteButton
            onPaste={async () => {
              try {
                const text = await navigator.clipboard.readText();
                setHash(normalizeHash(text));
                setError(null);
              } catch {
                setError('Не удалось прочитать буфер обмена. Вставьте код вручную.');
              }
            }}
          />
        </div>
        {error && <p className="mt-2 text-sm text-red-400">{error}</p>}
        <button
          type="submit"
          disabled={submitting || !isValid || support === 'unsupported'}
          className="mt-6 w-full rounded-lg bg-brand py-2 text-sm font-medium text-white shadow hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? 'Отправляем…' : 'Отправить код'}
        </button>
      </form>
    </Centered>
  );
}

function WaitingScreen({
  expiresAt,
  onCancel,
}: {
  expiresAt: number;
  onCancel: () => void;
}) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(id);
  }, []);
  const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
  return (
    <Centered>
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900/60 p-8 text-center">
        <Spinner />
        <h1 className="mt-4 text-xl font-semibold">Ожидаем подтверждение</h1>
        <p className="mt-2 text-sm text-slate-400">
          Администратор получил уведомление в Telegram. Дождитесь, пока запрос будет одобрен.
        </p>
        <p className="mt-3 text-xs text-slate-500">
          Можно закрыть вкладку и вернуться по той же ссылке — мы продолжим ждать подтверждение.
        </p>
        <div className="mt-6 font-mono text-sm text-slate-500">
          Истекает через {formatDuration(remaining)}
        </div>
        <button
          className="mt-6 text-sm text-slate-400 underline hover:text-slate-200"
          onClick={onCancel}
        >
          Отменить
        </button>
      </div>
    </Centered>
  );
}

function PasteButton({ onPaste }: { onPaste: () => Promise<void> }) {
  const [pasted, setPasted] = useState(false);
  const handle = async () => {
    await onPaste();
    setPasted(true);
    window.setTimeout(() => setPasted(false), 1_500);
  };
  return (
    <button
      type="button"
      onClick={handle}
      aria-label="Вставить код из буфера обмена"
      title="Вставить из буфера"
      className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-slate-400 hover:bg-slate-800 hover:text-brand"
    >
      {pasted ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="8" y="2" width="8" height="4" rx="1" />
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
        </svg>
      )}
    </button>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center px-4 py-12">{children}</div>;
}

function Spinner() {
  return (
    <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-slate-700 border-t-brand" />
  );
}

function normalizeHash(input: string): string {
  const cleaned = input.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 16);
  const groups = cleaned.match(/.{1,4}/g) ?? [];
  return groups.join('-');
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

function guessDeviceName(): string {
  const ua = navigator.userAgent;
  const match = /(Firefox|Chrome|Safari|Edg|OPR)\/[\d.]+/.exec(ua);
  const browser = match?.[1] ?? 'Browser';
  const os = /Mac OS X|Windows NT|Linux|Android|iPhone|iPad/.exec(ua)?.[0] ?? '';
  return os ? `${browser} (${os})` : browser;
}
